import { useParams } from "@tanstack/react-router";
import { LayoutDashboard } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { SHOP_VERIFICATION_POLICY } from "@/modules/merchant/domain/contracts/shop-verification";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { ShopDashboardAttentionSection } from "@/modules/merchant/presentation/ui/ShopDashboardAttentionSection";
import { ShopDashboardQuickActions } from "@/modules/merchant/presentation/ui/ShopDashboardQuickActions";
import { ShopDashboardSalesSummaryStrip } from "@/modules/merchant/presentation/ui/ShopDashboardSalesSummaryStrip";
import { PosSalesByCurrencyReportSection } from "@/modules/merchant/presentation/ui/PosSalesByCurrencyReportSection";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";

export function ShopDashboardPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const { currentShop } = useMerchantShops(shopId);
  const verified = currentShop?.verifiedLevel ?? null;
  const perms = currentShop?.permissions ?? [];

  const canReports = canUseMerchantPermission(
    perms,
    MerchantPermissions.pos.read,
    verified,
  );

  const vLevel = currentShop?.verifiedLevel ?? "none";
  const verificationLabel =
    vLevel === "none"
      ? null
      : SHOP_VERIFICATION_POLICY[vLevel]?.label ?? null;

  return (
    <>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.dashboard()} />
      </Header>

      <Main>
        <section
          className="relative mb-6 overflow-hidden rounded-2xl border border-border/80 bg-linear-to-br from-primary/[0.07] via-background to-muted/40 p-6 shadow-sm sm:p-8"
          aria-labelledby="shop-dashboard-title"
        >
          <div className="pointer-events-none absolute -right-16 -top-16 size-48 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/80 px-3 py-1 text-muted-foreground text-xs shadow-sm backdrop-blur-sm">
                <LayoutDashboard className="size-3.5 text-primary" aria-hidden />
                <span>ແຜງຄວບຄຸມຮ້ານ</span>
              </div>
              <h1
                id="shop-dashboard-title"
                className="font-heading font-semibold text-2xl text-foreground tracking-tight sm:text-3xl"
              >
                {currentShop?.shopName ?? shopId ?? "—"}
              </h1>
              <p className="max-w-xl text-muted-foreground text-sm leading-relaxed">
                ສະຫຼຸບການຂາຍ ແລະ ວຽກທີ່ຄວນສົນໃຈ — ເລືອກວຽກດ່ວນດ້ານລຸ່ມ
              </p>
            </div>
            {verificationLabel ? (
              <div className="shrink-0 rounded-xl border border-border/70 bg-background/90 px-4 py-2.5 text-center shadow-sm backdrop-blur-sm sm:text-left">
                <p className="font-medium text-[10px] text-muted-foreground uppercase tracking-wider">
                  ສະຖານະຮ້ານ
                </p>
                <p className="mt-0.5 font-medium text-foreground text-sm">
                  {verificationLabel}
                </p>
              </div>
            ) : null}
          </div>
        </section>

        <ShopDashboardQuickActions
          shopId={shopId}
          permissions={perms}
          verifiedLevel={verified}
          featureFlags={currentShop?.featureFlags}
        />

        <div className="space-y-6">
          <ShopDashboardAttentionSection shopId={shopId} />
          <ShopDashboardSalesSummaryStrip shopId={shopId} canView={canReports} />
          <PosSalesByCurrencyReportSection shopId={shopId} />
        </div>
      </Main>
    </>
  );
}
